import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function makeSessionId() {
  try {
    const k = "ml_chat_sid";
    let sid = localStorage.getItem(k);
    if (!sid) {
      sid =
        "sid-" +
        Math.random().toString(36).slice(2, 10) +
        "-" +
        Date.now().toString(36);
      localStorage.setItem(k, sid);
    }
    return sid;
  } catch {
    return "sid-anon-" + Date.now().toString(36);
  }
}

const SUGGESTIONS = [
  "How much does LVP cost?",
  "Do you serve O'Fallon?",
  "LVP vs Laminate — which is better?",
  "Do you install bathroom tile?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm the M&L flooring assistant. Ask me anything about LVP, laminate, tile, pricing, or our service areas.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(makeSessionId);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const openHandler = () => setOpen((v) => !v);
    const closeHandler = () => setOpen(false);
    window.addEventListener("open-chat", openHandler);
    window.addEventListener("close-chat", closeHandler);
    return () => {
      window.removeEventListener("open-chat", openHandler);
      window.removeEventListener("close-chat", closeHandler);
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setInput("");
    setSending(true);
    try {
      const resp = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: trimmed }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Chat error");
      }
      const data = await resp.json();
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry — I'm having trouble right now. Please call us at 636-849-6681 and we'll help right away.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Chat with M&L flooring assistant"
      data-testid="chat-window"
      className="fixed z-[89] bottom-40 sm:bottom-24 left-4 sm:left-6 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[420px] h-[560px] max-h-[calc(100vh-10rem)] bg-stone-50 rounded-3xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)] border border-stone-200 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="bg-stone-900 text-stone-50 px-5 py-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center">
          <MessageCircle size={16} strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm leading-tight">
            M&amp;L Flooring Assistant
          </div>
          <div className="text-[11px] text-stone-300 leading-tight flex items-center gap-1.5 mt-0.5">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
            Online · Answers in seconds
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="chat-close"
          aria-label="Close chat"
          className="w-8 h-8 rounded-full bg-stone-800 hover:bg-stone-700 flex items-center justify-center transition-colors"
        >
          <X size={16} strokeWidth={2.25} />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        data-testid="chat-messages"
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-stone-50"
      >
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`chat-msg-${m.role}`}
            className={`flex ${
              m.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-orange-500 text-stone-50 rounded-br-md"
                  : "bg-white border border-stone-200 text-stone-800 rounded-bl-md"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="chat-typing">
            <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5">
              <span className="w-2 h-2 bg-stone-400 rounded-full animate-pulse [animation-delay:0ms]" />
              <span className="w-2 h-2 bg-stone-400 rounded-full animate-pulse [animation-delay:200ms]" />
              <span className="w-2 h-2 bg-stone-400 rounded-full animate-pulse [animation-delay:400ms]" />
            </div>
          </div>
        )}

        {messages.length === 1 && !sending && (
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-2">
              Try asking:
            </div>
            <div className="flex flex-col gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid={`chat-suggestion-${s
                    .slice(0, 20)
                    .replace(/[^a-z0-9]+/gi, "-")
                    .toLowerCase()}`}
                  className="text-left text-sm text-stone-700 bg-white border border-stone-200 hover:border-orange-500 hover:bg-orange-50/50 rounded-xl px-3 py-2 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-stone-200 bg-white p-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            data-testid="chat-input"
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask about LVP, tile, pricing, service areas…"
            className="flex-1 resize-none rounded-2xl border border-stone-200 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 px-3.5 py-2.5 text-sm text-stone-800 max-h-32"
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={!input.trim() || sending}
            data-testid="chat-send"
            aria-label="Send message"
            className="w-11 h-11 rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors shrink-0"
          >
            {sending ? (
              <Loader2 size={16} className="text-stone-50 animate-spin" />
            ) : (
              <Send size={16} className="text-stone-50" strokeWidth={2.25} />
            )}
          </button>
        </div>
        <p className="mt-2 text-[10px] text-stone-500 text-center">
          AI can make mistakes — call{" "}
          <a
            href="tel:+16368496681"
            className="font-semibold text-orange-600"
          >
            636-849-6681
          </a>{" "}
          for anything critical.
        </p>
      </div>
    </div>
  );
}
